import { cn } from "../lib/utils";

/** One segment. `shortLabel` is the visible chip text; `label` is the a11y name. */
export interface SegmentedOption {
  /** Stable id passed back to `onChange`. */
  id: string;
  /** Accessible name (long form). */
  label: string;
  /** Visible chip text; falls back to `label` when omitted. */
  shortLabel?: string;
}

export interface SegmentedControlProps {
  options: SegmentedOption[];
  /** Id of the active segment. */
  value: string;
  onChange: (id: string) => void;
  /** Accessible name for the button group. */
  groupLabel: string;
  /** Disable the whole control while a fetch is in flight. */
  disabled?: boolean;
  className?: string;
}

// ponytail: `border-border` hairline container — the UI-SPEC names a "border-strong"
// token but plan 01 didn't ship the utility (same gap pagination.tsx notes). Swap
// the container border to `border-strong` when that token lands.
const container =
  "rounded-segment border-border inline-flex items-center gap-0.5 border p-0.5";

const segment =
  "rounded-segment focus-visible:ring-ring inline-flex items-center justify-center px-2.5 py-1 text-[13px] font-semibold transition-colors duration-150 outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none";

/**
 * The Atlas segmented control (audit D5), extracted from RangePresetGroup's
 * role="group" + aria-pressed pattern so the shape lives once. The active segment
 * is ink-filled (`bg-foreground text-background`) per the Atlas "active = ink"
 * language (Tabs/Pagination share it); inactive segments are muted. Presentational
 * and i18n-free — every label arrives via props (text-only children, T-09.3-16).
 */
export function SegmentedControl({
  options,
  value,
  onChange,
  groupLabel,
  disabled = false,
  className,
}: SegmentedControlProps) {
  return (
    <div
      role="group"
      aria-label={groupLabel}
      data-slot="segmented-control"
      className={cn(container, className)}
    >
      {options.map((option) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={active}
            aria-label={option.label}
            disabled={disabled}
            onClick={() => onChange(option.id)}
            className={cn(
              segment,
              active
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.shortLabel ?? option.label}
          </button>
        );
      })}
    </div>
  );
}
